import React, { useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  RecordVoiceOverOutlined,
  PlayCircleOutlineOutlined,
  PauseCircleOutlineOutlined,
} from "@mui/icons-material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { REALTIME_VOICES } from "../../helpers/realtimeVoices";

const VoiceSelect = ({ promptId, value, onChange, disabled, error, helperText }) => {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [promptId, value]);

  const detener = () => {
    audioRef.current?.pause();
    setPlaying(false);
  };

  const escuchar = async () => {
    if (playing) {
      detener();
      return;
    }

    if (!promptId || !value) return;

    setLoading(true);
    try {
      const { data } = await api.post(
        `/prompt/${promptId}/voice-preview`,
        { voice: value },
        { responseType: "blob" }
      );

      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      const url = URL.createObjectURL(data);
      urlRef.current = url;

      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.play();
      setPlaying(true);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const tooltip = !promptId
    ? "Guardá el Prompt primero"
    : !value
    ? "Elegí una voz primero"
    : playing
    ? "Detener"
    : "Escuchar esta voz";

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
      <TextField
        select
        label="Voz"
        size="small"
        variant="outlined"
        fullWidth
        disabled={disabled}
        error={error}
        helperText={helperText}
        value={value}
        onChange={(e) => {
          detener();
          onChange(e.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <RecordVoiceOverOutlined fontSize="small" color="disabled" />
            </InputAdornment>
          ),
        }}
      >
        <MenuItem value="">
          <em>Sin elegir</em>
        </MenuItem>
        {REALTIME_VOICES.map((v) => (
          <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
        ))}
      </TextField>

      <Tooltip title={tooltip}>
        <span>
          <IconButton
            size="small"
            disabled={!promptId || !value || loading}
            onClick={escuchar}
            style={{ marginTop: 4 }}
          >
            {loading ? (
              <CircularProgress size={18} />
            ) : playing ? (
              <PauseCircleOutlineOutlined />
            ) : (
              <PlayCircleOutlineOutlined />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

export default VoiceSelect;
